import { useCallback, useState } from 'react';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useUpdateTest } from '@/features/tests/hooks/tests';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { TestEditValues } from '@/features/tests/components/TestsTable.types';
import type { TagColor } from '@/shared/data/tags';
import type { TestListItemDto } from '@flowqa/shared';

type TestsTableEditDialog = {
  editEditor: ReturnType<typeof useDetailEditEditor<string>>;
  editErrors: ReturnType<typeof useFormErrors<TestEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<TestEditValues>>;
  editTarget: TestListItemDto | null;
  handleEditClose: () => void;
  handleEditRow: (row: TestListItemDto) => void;
  handleEditSave: (input: { tags: string[]; colors: TagColor[] }) => Promise<void>;
};

export const useTestsTableEditDialog = (): TestsTableEditDialog => {
  const toErrorMessage = useApiErrorMessage();
  const [editTarget, setEditTarget] = useState<TestListItemDto | null>(null);
  const editForm = useDialogFormState<TestEditValues>({ name: '', projectId: '', suiteIds: [] });
  const editErrors = useFormErrors(editForm);
  const updateTest = useUpdateTest();
  const editEditor = useDetailEditEditor({
    projectId: editTarget?.projectId ?? null,
    successMessage: getToastMessage('entity.updated', {
      entity: 'test',
      name: editTarget?.name,
    }),
    saveEntity: async ({ name, tags }) => {
      if (!editTarget) return;
      await updateTest.mutateAsync({
        id: editTarget.id,
        name: name ?? editForm.values.name,
        suiteIds: editForm.values.suiteIds,
        tags,
      });
    },
  });

  const handleEditRow = useCallback(
    (row: TestListItemDto): void => {
      setEditTarget(row);
      editForm.setField('name', row.name);
      editForm.setField('projectId', row.projectId);
      editForm.setField('suiteIds', row.suites?.ids ?? []);
    },
    [editForm],
  );

  const handleEditClose = useCallback((): void => {
    if (editEditor.isPending) return;
    editForm.handleClose(() => {
      setEditTarget(null);
    });
  }, [editEditor.isPending, editForm]);

  const handleEditSave = useCallback(
    async ({ tags, colors }: { tags: string[]; colors: TagColor[] }): Promise<void> => {
      if (!editTarget) return;
      const valid = editForm.validateRequired('name');
      if (!valid) return;
      try {
        await editEditor.save({
          name: editForm.values.name,
          tags,
          colors,
        });
        editForm.handleClose(() => {
          setEditTarget(null);
        });
      } catch (error) {
        if (!editForm.applyApiErrors(error)) {
          editForm.setSubmitError(toErrorMessage(error));
        }
      }
    },
    [editEditor, editForm, editTarget, toErrorMessage],
  );

  return {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  };
};
